/**
 * Image component for canvas rendering.
 * Displays images with alt text and configurable sizing.
 */

import React, { memo, useMemo, useState } from 'react';
import { ImageOff } from 'lucide-react';
import type { ImageProps as ImagePropsType } from '../../../types/canvas';

const AGENT_BASE = (process.env.REACT_APP_AGENT_URL || 'http://localhost:8090').replace(
  /\/$/,
  '',
);

/** Orchestrator serves public ML artifacts at /ws-files/... and authenticated workspace files at /files/...; backend may send path-only so we prepend REACT_APP_AGENT_URL. */
function resolveImageSrc(raw: string): string {
  if (!raw) return raw;
  if (raw.startsWith('/')) {
    return `${AGENT_BASE}${raw}`;
  }
  return raw;
}

export interface ImageComponentProps extends ImagePropsType {}

function ImageComponentBase({
  src,
  alt,
  width,
  height,
  objectFit = 'contain',
}: ImageComponentProps) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const resolvedSrc = useMemo(() => resolveImageSrc(src), [src]);

  const objectFitStyles = {
    contain: 'object-contain',
    cover: 'object-cover',
    fill: 'object-fill',
    none: 'object-none',
  };

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-400 dark:text-gray-300 p-4"
        style={{ width: width || '100%', height: height || '200px' }}
      >
        <ImageOff size={32} />
        <span className="mt-2 text-sm">Failed to load image</span>
      </div>
    );
  }

  return (
    <div className="relative" style={{ width: width || '100%', height: height || 'auto' }}>
      {!loaded && (
        <div
          className="absolute inset-0 bg-gray-100 dark:bg-gray-800 animate-pulse rounded-lg"
          style={{ height: height || '200px' }}
        />
      )}
      <img
        src={resolvedSrc}
        alt={alt}
        onError={() => setError(true)}
        onLoad={() => setLoaded(true)}
        className={`rounded-lg ${objectFitStyles[objectFit]} ${!loaded ? 'opacity-0' : 'opacity-100'} transition-opacity`}
        style={{
          width: width || '100%',
          height: height || 'auto',
          maxWidth: '100%',
        }}
      />
    </div>
  );
}

export const ImageComponent = memo(ImageComponentBase);
